<script setup>
import { computed, ref, onMounted } from 'vue';
import { useTasksStore } from '@/stores/tasks.js';
import { useAuthStore } from '@/stores/auth.js';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Message from 'primevue/message';

const props = defineProps({ taskId: { type: [Number, String], required: true } });
const tasks = useTasksStore();
const auth = useAuthStore();
const toast = useToast();

const loading = ref(false);
const err404 = ref(false);

const myId = computed(() => auth?.me?.id);
const task = computed(() => tasks.taskById(props.taskId));

async function load() {
  loading.value = true;
  err404.value = false;
  try { await tasks.loadOne(props.taskId); }
  catch (e) {
    if (e?.response?.status === 404) { err404.value = true; toast.add({ severity: 'warn', summary: 'Задача не найдена', life: 3000 }); }
    else { toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить задачу', life: 4000 }); }
  }
  finally { loading.value = false; }
}

onMounted(load);

const isAssignee = computed(() => {
  if (!task.value || !myId.value) return false;
  return Number(task.value.assignee) === Number(myId.value);
});

const isCreator = computed(() => {
  if (!task.value || !myId.value) return false;
  return Number(task.value.creator) === Number(myId.value);
});

const canDone = computed(() => {
  return isAssignee.value && task.value && ['ASSIGNED','RETURNED'].includes(task.value.status);
});

const canDecline = computed(() => {
  return isAssignee.value && task.value && ['ASSIGNED','RETURNED'].includes(task.value.status);
});

const canReturn = computed(() => {
  return isCreator.value && task.value && task.value.status === 'DONE';
});

const canClose = computed(() => {
  return isCreator.value && task.value && task.value.status === 'DONE';
});

// Inline-форма для отклонения/возврата
const showReasonForm = ref(false);
const reasonAction = ref(''); // 'decline' или 'return'
const reason = ref('');
const reasonError = ref('');

async function actionDone() {
  try {
    await tasks.done(props.taskId);
    toast.add({ severity: 'success', summary: 'Отмечено как выполнено', life: 2000 });
  } catch (e) {
    handleActionError(e);
  }
}

function openDeclineForm() {
  reasonAction.value = 'decline';
  reason.value = '';
  reasonError.value = '';
  showReasonForm.value = true;
}

function openReturnForm() {
  reasonAction.value = 'return';
  reason.value = '';
  reasonError.value = '';
  showReasonForm.value = true;
}

function cancelReason() {
  showReasonForm.value = false;
  reason.value = '';
  reasonError.value = '';
  reasonAction.value = '';
}

async function submitReason() {
  reasonError.value = '';
  const r = reason.value?.trim();
  if (!r) {
    reasonError.value = 'Причина обязательна';
    return;
  }
  try {
    if (reasonAction.value === 'decline') {
      await tasks.decline(props.taskId, r);
      toast.add({ severity: 'success', summary: 'Задача отклонена', life: 2000 });
    } else if (reasonAction.value === 'return') {
      await tasks.returnTask(props.taskId, r);
      toast.add({ severity: 'success', summary: 'Задача возвращена', life: 2000 });
    }
    showReasonForm.value = false;
    reason.value = '';
    reasonAction.value = '';
  } catch (e) {
    handleActionError(e);
  }
}

async function actionClose() {
  try {
    await tasks.close(props.taskId);
    toast.add({ severity: 'success', summary: 'Задача закрыта', life: 2000 });
  } catch (e) {
    handleActionError(e);
  }
}

function handleActionError(e) {
  const s = e?.response?.status;
  if (s === 400) {
    const detail = e?.response?.data?.detail || e?.response?.data?.error || 'Ошибка валидации';
    toast.add({ severity: 'warn', summary: 'Ошибка', detail, life: 4000 });
  } else if (s === 403) {
    toast.add({ severity: 'error', summary: 'Недостаточно прав', life: 4000 });
  } else if (s === 404) {
    toast.add({ severity: 'warn', summary: 'Задача не найдена', life: 3000 });
  } else {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось выполнить действие', life: 4000 });
  }
}
</script>

<template>
  <div class="space-y-2">
    <div v-if="loading">Загрузка…</div>
    <div v-else-if="err404">Задача не найдена</div>
    <div v-else-if="task">
      <div>{{ task.description }}</div>

      <!-- Inline-форма для отклонения/возврата -->
      <div v-if="showReasonForm" class="mt-3 pl-3 border-l border-surface-200">
        <form @submit.prevent="submitReason" class="flex flex-col gap-2">
          <label class="text-sm font-semibold">
            {{ reasonAction === 'decline' ? 'Причина отклонения' : 'Причина возврата' }} (обязательно)
          </label>
          <Textarea v-model="reason" rows="3" autoResize placeholder="Укажите причину..." style="resize:none" />
          <Message v-if="reasonError" severity="error" size="small" variant="simple">{{ reasonError }}</Message>
          <div class="flex gap-2">
            <Button type="submit" label="Отправить" severity="info" size="small" />
            <Button type="button" label="Отмена" severity="secondary" size="small" outlined @click="cancelReason" />
          </div>
        </form>
      </div>

      <!-- Отображение причин отклонения/возврата -->
      <div v-if="task.decline_reason" class="mt-3 pl-3 border-l-2 border-red-400">
        <div class="text-sm">
          <span class="font-semibold text-red-600">Причина отклонения:</span>
          <div class="whitespace-pre-line mt-1">{{ task.decline_reason }}</div>
        </div>
      </div>

      <div v-if="task.return_reason" class="mt-3 pl-3 border-l-2 border-yellow-400">
        <div class="text-sm">
          <span class="font-semibold text-yellow-600">Причина возврата:</span>
          <div class="whitespace-pre-line mt-1">{{ task.return_reason }}</div>
        </div>
      </div>

      <!-- Действия -->
      <div class="flex flex-wrap gap-2 pt-2 mt-2 border-t border-surface-200">
        <Button v-if="canDone" label="Готово" icon="pi pi-check" severity="success" variant="text" size="small" @click.stop="actionDone" />
        <Button v-if="canDecline" label="Отклонить" icon="pi pi-times" severity="danger" variant="text" size="small" @click.stop="openDeclineForm" />
        <Button v-if="canReturn" label="Вернуть" icon="pi pi-undo" severity="warn" variant="text" size="small" @click.stop="openReturnForm" />
        <Button v-if="canClose" label="Закрыть" icon="pi pi-lock" severity="secondary" variant="text" size="small" @click.stop="actionClose" />
      </div>
    </div>
  </div>
</template>